import { z } from 'zod';

// Common schemas
const navItemSchema = z.object({
  label: z.string().min(1),
  link: z.string(),
  children: z.array(z.lazy(() => navItemSchema)).optional(),
});

const ctaButtonSchema = z.object({
  text: z.string().min(1),
  link: z.string(),
  variant: z.enum(['primary', 'secondary', 'outline']),
});

const formFieldSchema = z.object({
  name: z.string().min(1),
  label: z.string().min(1),
  type: z.enum(['text', 'email', 'phone', 'textarea', 'select']),
  required: z.boolean(),
  options: z.array(z.string()).optional(),
});

const dayHoursSchema = z.object({
  open: z.string(),
  close: z.string(),
  closed: z.boolean().optional(),
});

const workingHoursSchema = z.object({
  monday: dayHoursSchema.optional(),
  tuesday: dayHoursSchema.optional(),
  wednesday: dayHoursSchema.optional(),
  thursday: dayHoursSchema.optional(),
  friday: dayHoursSchema.optional(),
  saturday: dayHoursSchema.optional(),
  sunday: dayHoursSchema.optional(),
});

// Block content schemas
export const blockSchemas = {
  header: z.object({
    logo: z.string().optional(),
    logoText: z.string().optional(),
    navigation: z.array(navItemSchema),
    showCta: z.boolean().optional(),
    ctaText: z.string().optional(),
    ctaLink: z.string().optional(),
    style: z.enum(['simple', 'centered', 'split']),
  }),

  hero: z.object({
    title: z.string().min(1),
    subtitle: z.string().optional(),
    description: z.string().optional(),
    backgroundImage: z.string().optional(),
    backgroundVideo: z.string().optional(),
    overlay: z.boolean().optional(),
    overlayOpacity: z.number().min(0).max(1).optional(),
    ctaButtons: z.array(ctaButtonSchema).optional(),
    style: z.enum(['centered', 'left', 'right', 'split']),
    height: z.enum(['full', 'large', 'medium', 'small']),
  }),

  text: z.object({
    content: z.string(),
    alignment: z.enum(['left', 'center', 'right']),
  }),

  image: z.object({
    src: z.string(),
    alt: z.string(),
    caption: z.string().optional(),
    link: z.string().optional(),
    aspectRatio: z.enum(['auto', '1:1', '4:3', '16:9', '21:9']).optional(),
  }),

  gallery: z.object({
    images: z.array(z.object({
      src: z.string(),
      alt: z.string(),
      caption: z.string().optional(),
    })),
    columns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
    style: z.enum(['grid', 'masonry', 'carousel']),
    lightbox: z.boolean().optional(),
  }),

  video: z.object({
    url: z.string(),
    provider: z.enum(['youtube', 'vimeo', 'direct']),
    autoplay: z.boolean().optional(),
    controls: z.boolean().optional(),
    loop: z.boolean().optional(),
    muted: z.boolean().optional(),
  }),

  features: z.object({
    title: z.string().optional(),
    subtitle: z.string().optional(),
    features: z.array(z.object({
      icon: z.string().optional(),
      title: z.string(),
      description: z.string(),
      link: z.string().optional(),
    })),
    columns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
    style: z.enum(['cards', 'icons', 'minimal']),
  }),

  services: z.object({
    title: z.string().optional(),
    subtitle: z.string().optional(),
    services: z.array(z.object({
      image: z.string().optional(),
      icon: z.string().optional(),
      title: z.string(),
      description: z.string(),
      price: z.string().optional(),
      link: z.string().optional(),
    })),
    columns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
    style: z.enum(['cards', 'list', 'grid']),
  }),

  testimonials: z.object({
    title: z.string().optional(),
    testimonials: z.array(z.object({
      content: z.string(),
      author: z.string(),
      role: z.string().optional(),
      company: z.string().optional(),
      avatar: z.string().optional(),
      rating: z.number().min(1).max(5).optional(),
    })),
    style: z.enum(['cards', 'carousel', 'minimal']),
  }),

  team: z.object({
    title: z.string().optional(),
    subtitle: z.string().optional(),
    members: z.array(z.object({
      photo: z.string(),
      name: z.string(),
      role: z.string(),
      bio: z.string().optional(),
      social: z.object({
        facebook: z.string().optional(),
        instagram: z.string().optional(),
        linkedin: z.string().optional(),
        twitter: z.string().optional(),
      }).optional(),
    })),
    columns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
  }),

  pricing: z.object({
    title: z.string().optional(),
    subtitle: z.string().optional(),
    plans: z.array(z.object({
      name: z.string(),
      price: z.string(),
      period: z.string().optional(),
      description: z.string().optional(),
      features: z.array(z.string()),
      ctaText: z.string(),
      ctaLink: z.string(),
      highlighted: z.boolean().optional(),
    })),
    style: z.enum(['cards', 'table']),
  }),

  faq: z.object({
    title: z.string().optional(),
    faqs: z.array(z.object({
      question: z.string(),
      answer: z.string(),
    })),
    style: z.enum(['accordion', 'list']),
  }),

  contact: z.object({
    title: z.string().optional(),
    subtitle: z.string().optional(),
    showForm: z.boolean(),
    formFields: z.array(formFieldSchema),
    showInfo: z.boolean(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    whatsapp: z.string().optional(),
    address: z.string().optional(),
  }),

  map: z.object({
    address: z.string(),
    lat: z.number().optional(),
    lng: z.number().optional(),
    zoom: z.number().min(1).max(20),
    height: z.number().min(100).max(800),
    showMarker: z.boolean(),
  }),

  cta: z.object({
    title: z.string(),
    description: z.string().optional(),
    buttons: z.array(ctaButtonSchema),
    style: z.enum(['simple', 'split', 'centered']),
    backgroundImage: z.string().optional(),
  }),

  footer: z.object({
    logo: z.string().optional(),
    description: z.string().optional(),
    columns: z.array(z.object({
      title: z.string(),
      links: z.array(z.object({
        label: z.string(),
        url: z.string(),
      })),
    })),
    socialLinks: z.object({
      facebook: z.string().optional(),
      instagram: z.string().optional(),
      twitter: z.string().optional(),
      linkedin: z.string().optional(),
      youtube: z.string().optional(),
    }).optional(),
    copyright: z.string().optional(),
    style: z.enum(['simple', 'multi-column', 'centered']),
  }),

  menu: z.object({
    title: z.string().optional(),
    categories: z.array(z.object({
      name: z.string(),
      description: z.string().optional(),
      items: z.array(z.object({
        name: z.string(),
        description: z.string().optional(),
        price: z.number(),
        image: z.string().optional(),
        tags: z.array(z.string()).optional(),
        available: z.boolean(),
      })),
    })),
    style: z.enum(['simple', 'cards', 'detailed']),
    showPrices: z.boolean(),
    currency: z.string(),
  }),

  booking: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    services: z.array(z.object({
      name: z.string(),
      duration: z.number(),
      price: z.number(),
      description: z.string().optional(),
    })),
    showCalendar: z.boolean(),
    workingHours: workingHoursSchema.optional(),
  }),

  products: z.object({
    title: z.string().optional(),
    products: z.array(z.object({
      name: z.string(),
      description: z.string().optional(),
      price: z.number(),
      salePrice: z.number().optional(),
      image: z.string(),
      images: z.array(z.string()).optional(),
      link: z.string().optional(),
      badge: z.string().optional(),
      available: z.boolean(),
    })),
    columns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
    showPrices: z.boolean(),
    currency: z.string(),
    style: z.enum(['grid', 'list']),
  }),

  hours: z.object({
    title: z.string().optional(),
    hours: workingHoursSchema,
    showCurrentStatus: z.boolean(),
    timezone: z.string(),
  }),

  social: z.object({
    platform: z.enum(['instagram', 'facebook']),
    username: z.string().optional(),
    feedUrl: z.string().optional(),
    postsCount: z.number().min(1).max(12),
    columns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
  }),

  newsletter: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    placeholder: z.string().optional(),
    buttonText: z.string(),
    successMessage: z.string(),
  }),

  divider: z.object({
    style: z.enum(['line', 'dashed', 'dotted', 'gradient']),
    color: z.string().optional(),
    width: z.enum(['full', 'medium', 'small']),
  }),

  spacer: z.object({
    height: z.number().min(10).max(200),
    mobileHeight: z.number().min(10).max(200).optional(),
  }),

  html: z.object({
    code: z.string(),
    sandboxed: z.boolean().optional(),
  }),
};
